"use client"

import { useEffect, useState, useCallback } from "react"
import { ReactComponent as NigeriaSVG } from "../../assets/map.svg"
import stateData from "./data/states.json"
import "./NigeriaMap.css"
import TextFade from "../../components/CustomTexts/TextFade"
import TextEffect from "../CustomTexts/TextEffect"

interface StateData {
  id: string
  name: string
  info?: string
  ofsp?: boolean
}

const NigeriaMap = () => {
  const [hoveredStateId, setHoveredStateId] = useState<string | null>(null)
  const [selectedState, setSelectedState] = useState<StateData | null>(null)
  const [states, setStates] = useState<StateData[]>([])
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    setStates(stateData)
  }, [])

  const getStateDataById = useCallback(
    (id: string): StateData | null => states.find((s) => s.id === id) || null,
    [states]
  )

  const handleMouseOver = (e: React.MouseEvent<SVGElement>) => {
    const target = e.target as SVGPathElement
    const id = target.id
    if (id) {
      const rect = target.getBoundingClientRect()
      setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top })
      setHoveredStateId(id)
    }
  }

  const handleMouseOut = () => setHoveredStateId(null)

  const handleClick = (e: React.MouseEvent<SVGElement>) => {
    const target = e.target as SVGPathElement
    const id = target.id
    const data = getStateDataById(id)
    if (data) setSelectedState(data)
  }

  const closeModal = () => setSelectedState(null)

  // 🎨 Color scheme update
  useEffect(() => {
    states.forEach((state) => {
      const el = document.getElementById(state.id)
      if (el) {
        if (state.ofsp) el.style.fill = "#f59e0b" // Orange = OFSP
        else el.style.fill = "#006634" // Neutral gray
      }
    })
  }, [states])

  const dotData = [
    { id: "kogi", top: "62%", left: "31%", label: "Kogi" },
    { id: "fct", top: "55%", left: "37%", label: "F.C.T" },
    { id: "niger", top: "42%", left: "34%", label: "Niger" },
    { id: "benue", top: "70%", left: "50%", label: "Benue" },
    { id: "lagos", top: "75%", left: "10%", label: "Lagos" },
        { id: "borno", top: "14%", left: "90%", label: "Borno" },
    { id: "lagos", top: "75%", left: "10%", label: "Lagos" },
    { id: "imo", top: "90%", left: "35%", label: "Imo" },
            { id: "kebbi", top: "10%", left: "20%", label: "Kebbi" },
    { id: "nassarawa", top: "50%", left: "50%", label: "Nassarawa" },
    { id: "kano", top: "20%", left: "50%", label: "Kano" },
    { id: "crossriver", top: "79%", left: "50%", label: "Cross River" },
  ]

  return (
    <div className="bg-white overflow-hidden">
      <div className="flex w-full px-[5%] flex-col items-start justify-center py-12 bg-dark">
        <TextFade animation="fade-right">
          <h1 className="md:text-[40px] text-[25px] text-secondary">
            <TextEffect part1="What's" /> <em><TextEffect part1="New" /></em>
          </h1>
        </TextFade>
        <p className="text-light md:text-[18px] text-[14px] md:w-[45%] w-full mt-2 leading-relaxed">
          <TextFade animation="fade-right">
            Get to know our latest Orange-Fleshed Sweet Potato (OFSP) developments across Nigeria
          </TextFade>
        </p>
      </div>

      <div className="relative w-full flex justify-center items-center py-10 px-4">
        <div className="w-full max-w-6xl mx-auto relative flex flex-col md:flex-row gap-8 items-center justify-between">
          
          <div className="w-full md:w-[50%] text-left px-4 md:px-0 md:pl-8">
            <TextFade animation="fade-right">
              <h2 className="text-[24px] md:text-[40px] font-bold text-dark leading-tight mb-4">
                <TextEffect textt="Our National Presence" />
              </h2>
            </TextFade>
            <TextFade animation="fade-right">
              <p className="text-[14px] md:text-[18px] text-gray-700 leading-relaxed">
                Explore the states where our Orange-Fleshed Sweet Potato (OFSP) programs and value chain projects are active.
              </p>
            </TextFade>
          </div>

          <div className="nigeria-map-container relative w-full md:w-[55%] scale-[0.9] md:scale-[0.95] translate-x-2 md:translate-x-8">
            <NigeriaSVG
              className="w-full h-auto nigeria-map"
              onMouseOver={handleMouseOver}
              onMouseOut={handleMouseOut}
              onClick={handleClick}
              preserveAspectRatio="xMidYMid meet"
              viewBox="0 0 5114 4133"
            />

            {dotData.map((dot) => (
              <div
                key={dot.id}
                className="absolute w-3 h-3 bg-orange-500 rounded-full animate-ping"
                style={{ top: dot.top, left: dot.left, transform: "translate(-50%, -50%)" }}
              />
            ))}
            {dotData.map((dot) => (
              <div
                key={`${dot.id}-label`}
                className="absolute text-xs font-semibold text-orange-600"
                style={{ top: `calc(${dot.top} - 20px)`, left: dot.left, transform: "translateX(-50%)" }}
              >
                {dot.label}
              </div>
            ))}
          </div>

          {hoveredStateId && (
            <div
              className="fixed z-50 bg-black text-white text-xs px-2 py-1 rounded-md shadow"
              style={{
                left: tooltipPosition.x,
                top: tooltipPosition.y - 40,
                transform: "translateX(-50%)",
              }}
            >
              {getStateDataById(hoveredStateId)?.name}
            </div>
          )}

          {selectedState && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
              <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative">
                <button className="absolute top-2 right-2 text-xl text-gray-500 hover:text-red-500" onClick={closeModal}>
                  &times;
                </button>
                <h2 className="text-xl font-bold mb-2">{selectedState.name}</h2>
                <p className="text-gray-700">
                  OFSP: {selectedState.ofsp ? "Available ✅" : "Not Available ❌"}
                </p>
                {selectedState.info && <p className="mt-3">{selectedState.info}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default NigeriaMap
